import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { MdDelete, MdDownload } from 'react-icons/md';
import InfoTooltip from 'src/components/atoms/info-tooltip';
import { MarketplaceInfo } from 'src/api/v1-api/helpers';
import { useAuth } from 'src/utils/auth';
import { downloadJsonAsCsv } from 'src/utils/files';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
import useRequest from 'src/utils/hooks/useRequest';
import Link from 'next/link';

export interface AdvancedFormFields {
  sourceToListedVerb: Record<string, string>;
  textOverrides: Record<string, string>;
  defaultOverrides: Record<string, string>;
}

export interface AdvancedFormProps {
  marketplace: MarketplaceInfo | null;
  isLoading?: boolean;
  onSubmit: (data: AdvancedFormFields) => void;
}

export const AdvancedForm = ({ marketplace, isLoading, onSubmit }: AdvancedFormProps) => {
  const [source, setSource] = useState('');
  const [listedVerb, setListedVerb] = useState('');
  const [sourceToListedVerb, setSourceToListedVerb] = useState({} as Record<string, string>);
  const [textOverrides, setTextOverrides] = useState({} as Record<string, string>);
  const [defaultOverrides, setDefaultOverrides] = useState({} as Record<string, string>);
  const [isFetchingAnalytics, setIsFetchingAnalytics] = useState(false);

  const { api } = useRequest();
  const { hasStaff } = useAuth();
  const isStaff = hasStaff();
  const toastNotification = useSingleToast();

  useEffect(() => {
    if (marketplace) {
      setSourceToListedVerb(marketplace.functional?.source_to_listed_verb || {});
      setTextOverrides(marketplace.functional?.text_overrides || {});

      const overdefs = {} as Record<string, string>;
      // global defaults
      for (const textover of marketplace?.text_overrides ?? []) {
        overdefs[textover.name] = textover.default_value;
      }
      setDefaultOverrides(overdefs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace]);

  if (!marketplace) {
    return <div>Loading...</div>;
  }

  const sources = Object.keys(sourceToListedVerb);

  const handleRemoveSource = (sourceKey: string) => {
    setSourceToListedVerb((prev) => {
      const next = { ...prev };
      delete next[sourceKey];
      return next;
    });
  };

  const addListedVerb = () => {
    if (!source || !listedVerb) {
      return;
    }
    setSourceToListedVerb((prev) => ({ ...prev, [source]: listedVerb }));
    setSource('');
    setListedVerb('');
  };

  const handleDefaultOverrideChange = (overrideKey: string, value: string) => {
    setDefaultOverrides({
      ...defaultOverrides,
      [overrideKey]: value,
    });
  };

  const handleTextOverrideChange = (overrideKey: string, value: string) => {
    setTextOverrides({
      ...textOverrides,
      [overrideKey]: value,
    });
  };

  const fetchAnalytics = async () => {
    if (!marketplace) return;
    try {
      setIsFetchingAnalytics(true);
      return await api.marketplace.analytics(marketplace.id);
    } catch (error) {
      toastNotification({
        title: 'Error Fetching Analytics',
        description:
          'Something went wrong, please try again. Contact support if the issue persists.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error,
          level: 'error',
          extras: {
            marketplaceId: marketplace.id,
          },
        },
      });
      return null;
    } finally {
      setIsFetchingAnalytics(false);
    }
  };

  const donwloadListingViews = async () => {
    if (!marketplace) return;

    const analytics = await fetchAnalytics();
    const decoratedAnalytics = (analytics ?? []).map(({ cta_clicks, ...data }) => ({
      ...data,
      call_to_action_clicks: cta_clicks,
    }));
    if (!analytics) return;
    downloadJsonAsCsv(`marketplace-${marketplace.name}-listing-views.csv`)(decoratedAnalytics);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      sourceToListedVerb,
      textOverrides,
      defaultOverrides,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Stack justify="flex-start" align="flex-start" spacing="20px" w="100%">
        <Stack justify="flex-start" align="flex-start" spacing="20px" w="100%">
          <Text lineHeight="1.5" fontWeight="semibold" fontSize="16px">
            Source to Listed Verb
          </Text>
          <Text fontSize="sm" color="gray.600" mb="20px">
            Customize the verb used to describe the source of a listing
          </Text>
          <Flex
            flexDirection="row"
            w="100%"
            borderBottom="1px solid"
            borderColor="gray.200"
            py={2}
            gap={4}
            alignItems="center"
            fontWeight="semibold"
          >
            <Flex minW={{ base: '35%', md: '260px' }} pl={4}>
              Source
            </Flex>
            <Flex minW={{ base: '35%', md: '260px' }}>Listed Verb</Flex>
          </Flex>
          {sources.length > 0 &&
            sources.map((sourceKey) => (
              <Flex
                flexDirection="row"
                key={sourceKey}
                w="100%"
                borderBottom="1px solid"
                borderColor="gray.200"
                py={2}
                gap={4}
                alignItems="center"
              >
                <Flex minW={{ base: '35%', md: '260px' }} pl={4}>
                  {sourceKey}
                </Flex>
                <Flex minW={{ base: '35%', md: '260px' }}>
                  <Text flex={1} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {sourceToListedVerb[sourceKey]}
                  </Text>
                </Flex>
                <Flex flexGrow={1} justifyContent="flex-end">
                  <IconButton
                    colorScheme="red"
                    icon={<MdDelete />}
                    aria-label="Delete map"
                    onClick={() => handleRemoveSource(sourceKey)}
                  />
                </Flex>
              </Flex>
            ))}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="flex-start"
            align="flex-start"
            gap="20px"
            w="100%"
          >
            <FormControl>
              <Input
                placeholder="Source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <Input
                placeholder="Listed verb"
                value={listedVerb}
                onChange={(e) => setListedVerb(e.target.value)}
              />
            </FormControl>
            <Box pt={{ base: 10, md: 0 }}>
              <Flex align="center">
                <Button colorScheme="gray" onClick={addListedVerb}>
                  Add
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Stack>

        <Divider />

        {marketplace?.text_overrides &&
          marketplace.text_overrides.some(
            (override) => !(override.edit_level === 'staff' && !isStaff),
          ) && (
            <>
              <Stack justify="flex-start" align="flex-start" spacing="20px" w="100%">
                <Text lineHeight="1.5" fontWeight="semibold" fontSize="16px">
                  Text Overrides
                </Text>
                <Text fontSize="sm" color="gray.600" mb="20px">
                  Customize the text of your marketplace to make it more relevant to your audience
                </Text>
                {marketplace.text_overrides.map((override) => {
                  if (override.edit_level === 'staff' && !isStaff) return null;
                  const overrideValue =
                    override.config_type === 'marketplace'
                      ? textOverrides[override.name] ?? override.default_value
                      : override.default_value;

                  return (
                    <Stack
                      key={override.id}
                      direction="row"
                      justify="flex-start"
                      align="flex-start"
                      spacing="20px"
                      w="full"
                    >
                      {isStaff && (
                        <FormControl>
                          <FormLabel w="100%">
                            {override.label ?? override.name} (Global Default)
                            {override.description && (
                              <InfoTooltip description={override.description} />
                            )}
                          </FormLabel>
                          <Input
                            value={defaultOverrides[override.name] ?? ''}
                            onChange={(e) =>
                              handleDefaultOverrideChange(override.name, e.target.value)
                            }
                          />
                        </FormControl>
                      )}
                      {override.config_type === 'marketplace' && (
                        <FormControl>
                          <FormLabel>
                            {override.label ?? override.name}
                            {override.description && (
                              <InfoTooltip description={override.description} />
                            )}
                          </FormLabel>
                          <Input
                            value={overrideValue ?? ''}
                            onChange={(e) =>
                              handleTextOverrideChange(override.name, e.target.value)
                            }
                          />
                        </FormControl>
                      )}
                    </Stack>
                  );
                })}
              </Stack>

              <Divider />
            </>
          )}

        <Stack justify="flex-start" align="flex-start" spacing="20px" w="100%">
          <Text lineHeight="1.5" fontWeight="semibold" fontSize="16px">
            Analytics & Data Export
          </Text>
          <FormControl width="fit-content">
            <Button
              leftIcon={<MdDownload />}
              onClick={donwloadListingViews}
              colorScheme="gray"
              isLoading={isFetchingAnalytics}
            >
              Download Listing Views CSV
            </Button>
          </FormControl>
        </Stack>
      </Stack>
      <Flex direction={'row'} justifyContent={'flex-end'} alignItems={'center'} pt={10}>
        <Box display="flex" justifyContent="flex-end">
          <Link href="/">
            <Button variant="outline" colorScheme="primary" mr={4}>
              Cancel
            </Button>
          </Link>
          <Button isLoading={isLoading} colorScheme="primary" type="submit" data-test="submit-btn">
            Save
          </Button>
        </Box>
      </Flex>
    </form>
  );
};

export default AdvancedForm;
